import React, { useState } from 'react';
import {
  ExternalLink,
  Check,
  ThumbsUp,
  MessageSquare,
  BarChart2,
} from '../../../icons';
import { Card } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { canSubmitContentRow } from '../../utils/campaignDetailHelpers';
import { formatCompact } from '../../../lib/format';
import {
  useCampaignCapabilitiesStore,
  useCampaignDraftsStore,
  useCampaignEditorStore,
} from '@discovr/features';
import { METRICS, submitLabel } from './types';

interface ContentRowData {
  id: string;
  status: string;
  live_insights?: Record<string, unknown>;
  content_feedback?: string;
  content_meta?: { url?: string };
  [key: string]: unknown;
}

export function ContentRowDetail({
  c: cRaw,
  layout = 'card',
  hideReviewActions = false,
}: {
  c: unknown;
  layout?: 'card' | 'embed';
  hideReviewActions?: boolean;
}) {
  const c = cRaw as ContentRowData;
  const { permissions, handleAdminSubmitContent: _handleAdminSubmitContent, handleAdminReviewContent, handleSaveInsights } = useCampaignCapabilitiesStore();
  const { contentUrlDraft, setContentUrlDraft, insightInputs, setInsightInputs } = useCampaignDraftsStore();
  // liveAnalytics is local to each expanded row — fetched lazily on open.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [liveAnalytics] = useState<Record<string, any>>({});
  const [contentActionBusy] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [insightSaving] = useState<Record<string, any>>({});
  const handleAdminSubmitContent = (entryId: string) => {
    const draft = contentUrlDraft[entryId] ?? '';
    return _handleAdminSubmitContent?.(entryId, { url: draft });
  };
  const {
    contentSubmitOpen,
    setContentSubmitOpen: _setContentSubmitOpen,
    // Parked: content-review feedback/mode UI scaffolding, not yet wired here.
    contentReviewFeedback: _contentReviewFeedback,
    setContentReviewFeedback: _setContentReviewFeedback,
    contentReviewUiMode: _contentReviewUiMode,
    setContentReviewUiMode: _setContentReviewUiMode,
  } = useCampaignEditorStore();
  const setContentSubmitOpen = (id: string, v: boolean) => _setContentSubmitOpen(id, v);

  // Permissions gate sits below all hooks so hook call order stays stable
  // (Rules of Hooks). `permissions` is first dereferenced just below.
  if (!permissions) return null;

  const contentBusy = contentActionBusy ?? false;
  const contentPendingReview = c.status === 'content_pending';
  const isApproved = c.status === 'content_approved' || c.status === 'content_live';
  // V2: content_rejected dropped — only revision_requested signals re-edit needed.
  const needsEdit = c.status === 'content_revision_requested';
  const canSubmitContent = canSubmitContentRow(c) && permissions.canSubmitContentOnBehalf;
  const submitOpen = contentSubmitOpen[c.id] ?? false;

  // BE returns the content URL inline on `content_meta.url`
  // (batch-loaded from content_versions). The legacy top-level
  // `content_url` field has been dropped from response shapes; we
  // read only `content_meta.url` here.
  const cAny = c as typeof c & { content_meta?: { url?: string } };
  const cContentUrl = cAny.content_meta?.url ?? '';

  // Whether `inner` would render anything. In the embed slot (content-review
  // drawer) with nothing to show, we return null instead of an empty card.
  const savedInsights = (liveAnalytics[c.id] ?? c.live_insights ?? {}) as Record<string, unknown>;
  const hasSavedInsights = METRICS.some((m) => savedInsights[m.key] !== undefined);
  const editingInsights = insightInputs[c.id] !== undefined;
  const showApproveAction = contentPendingReview && permissions.canReviewContent && !hideReviewActions;
  const hasAnyContent =
    !!cContentUrl ||
    canSubmitContent ||
    hasSavedInsights ||
    editingInsights ||
    !!c.content_feedback ||
    showApproveAction ||
    isApproved;

  const inner = (
    <div className="space-y-4 p-5">
      {cContentUrl && (
        <div className="flex flex-col gap-2 sm:flex-row">
          <a
            href={cContentUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-xl border border-border px-3 py-2 text-caption-medium text-foreground hover:bg-accent"
          >
            <ExternalLink size={11} /> View Content (draft)
          </a>
        </div>
      )}

      {canSubmitContent && (
        <div>
          {!submitOpen ? (
            <Button
              variant="outline"
              type="button"
              onClick={() => setContentSubmitOpen(c.id, true)}
            >
              {submitLabel(!!cContentUrl)}
            </Button>
          ) : (
            <div className="space-y-3">
              <Input
                  type="url"
                  placeholder="https://…"
                  value={contentUrlDraft[c.id] ?? ''}
                  onChange={(e) =>
                    setContentUrlDraft(c.id, e.target.value)
                  }
                  className="w-full rounded-xl border border-border bg-muted px-3.5 py-2.5 text-body-sm-regular text-foreground placeholder:text-muted-foreground focus:bg-card focus:ring-2 focus:ring-ring focus:outline-none"
                />
              <div className="flex gap-2">
                <Button
                  variant="default"
                  type="button"
                  onClick={() => handleAdminSubmitContent(c.id)}
                  disabled={contentBusy}
                  className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2 text-body-sm-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {contentBusy ? (
                    <span className="size-4 animate-spin rounded-full border-2 border-border border-t-white" />
                  ) : null}
                  Submit Content
                </Button>
                <Button
                  variant="ghost"
                  type="button"
                  onClick={() => setContentSubmitOpen(c.id, false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {(() => {
        const saved = (liveAnalytics[c.id] ?? c.live_insights ?? {}) as Record<string, unknown>;
        const hasSaved = METRICS.some((m) => saved[m.key] !== undefined);
        const inputs = insightInputs[c.id] ?? {};
        const editing = insightInputs[c.id] !== undefined;
        if (!hasSaved && !editing) return null;
        return (
          <div className="rounded-xl border border-border">
            <div className="flex items-center justify-between border-b border-border px-5 py-3">
              <span className="text-[11px] font-bold tracking-wider text-muted-foreground uppercase">
                Analytics
              </span>
              {permissions.canEditLiveAnalytics && (
                <Button
                  variant="ghost"
                  type="button"
                  onClick={() => {
                    if (editing) {
                       
                      setInsightInputs(c.id, {});
                    } else {
                      const prefill: Record<string, string> = {};
                      for (const m of METRICS) {
                        const v = saved[m.key];
                        if (v !== undefined) prefill[m.key] = String(v);
                      }
                      setInsightInputs(c.id, prefill);
                    }
                  }}
                  className="text-[11px] font-bold text-foreground transition-colors hover:text-foreground"
                >
                  {editing ? 'Cancel' : 'Edit'}
                </Button>
              )}
            </div>

            {hasSaved && !editing && (
              <div className="grid grid-cols-2 divide-x divide-y divide-border sm:grid-cols-4">
                {METRICS.filter((m) => saved[m.key] !== undefined).map((m) => (
                  <div key={m.key} className="px-4 py-3">
                    <div className="mb-1 flex items-center gap-1.5 text-[10px] font-bold tracking-wider text-muted-foreground uppercase">
                      <span>{m.icon}</span> {m.label}
                    </div>
                    <div className="flex items-end gap-1 text-lg font-black text-foreground">
                      {formatCompact(Number(saved[m.key]))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {editing && (
              <div className="space-y-4 p-5">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {METRICS.map((m) => (
                    <div key={m.key}>
                      <label className="mb-1.5 block text-[10px] font-bold tracking-wider text-muted-foreground uppercase">
                        {m.icon} {m.label}
                      </label>
                      <Input
                        type="number"
                        placeholder="0"
                        value={inputs[m.key] ?? ''}
                        onChange={(e) =>
                          setInsightInputs(c.id, { ...(insightInputs[c.id] ?? {}), [m.key]: e.target.value })
                        }
                        className="w-full rounded-xl border border-border bg-muted px-3 py-2 text-body-sm-semibold text-foreground focus:bg-card focus:ring-2 focus:ring-ring focus:outline-none"
                      />
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <Button
                    variant="default"
                    type="button"
                    onClick={() => handleSaveInsights?.(c.id, insightInputs[c.id] ?? {})}
                    disabled={insightSaving[c.id]}
                    className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-body-sm-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
                  >
                    {insightSaving[c.id] ? (
                      <>
                        <span className="size-4 animate-spin rounded-full border-2 border-border border-t-white" />
                        Saving…
                      </>
                    ) : (
                      <>
                        <BarChart2 size={14} />
                        Save Analytics
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {c.content_feedback && (
        <div
          className={`rounded-xl border p-4 ${needsEdit ? 'border-border-destructive-default bg-background-destructive-light-hover' : 'border-border-success-default bg-background-success-light-hover'}`}
        >
          <div
            className={`mb-1.5 flex items-center gap-1.5 text-[10px] font-bold tracking-wider uppercase ${needsEdit ? 'text-foreground-destructive-default' : 'text-foreground-success-default'}`}
          >
            <MessageSquare size={11} /> Previous Feedback
          </div>
          <p
            className={`text-body-sm-regular leading-relaxed ${needsEdit ? 'text-foreground-destructive-default' : 'text-foreground-success-default'}`}
          >
            {c.content_feedback}
          </p>
        </div>
      )}

      {contentPendingReview && permissions.canReviewContent && !hideReviewActions && (
        <div className="space-y-4 border-t border-border pt-5">
          {/* V2 (2026-05-05): brand has exactly one button — Approve. To
              request changes, brand types in the review thread; admin
              uploads a fresh content version off the back of that
              conversation. No "request revision" mode, no rejection. */}
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="default"
              type="button"
              disabled={contentBusy}
              onClick={() => handleAdminReviewContent?.(c.id, 'approve')}
              className="inline-flex items-center gap-2 rounded-xl bg-foreground-success-default px-4 py-2 text-body-sm-semibold text-white hover:bg-foreground-success-default/90 disabled:opacity-50"
            >
              <ThumbsUp size={14} /> Approve
            </Button>
            <p className="text-caption-regular text-muted-foreground">
              Need changes? Open the review thread and message the creator.
            </p>
          </div>
        </div>
      )}

      {isApproved && (
        <div className="flex items-center gap-2 rounded-xl border border-border-success-default bg-background-success-light-hover px-4 py-2.5">
          <Check size={14} className="text-foreground-success-default" />
          <span className="text-body-sm-semibold text-foreground-success-default">
            {c.status === 'content_live' ? 'Content is live ✨' : 'Content approved'}
          </span>
        </div>
      )}
    </div>
  );

  if (layout === 'embed') {
    if (!hasAnyContent) return null;
    return <div className="rounded-xl border border-border bg-card">{inner}</div>;
  }

  return <Card className="p-0 mx-4 my-3 border-border shadow-none">{inner}</Card>;
}
